import { InjectionToken } from '@angular/core';
import { LogLevel } from './log-level';

/**
 * Where telemetry goes.
 *
 * **Structural on purpose** — it names no vendor type, so `@pcp/logging` stays free of any
 * Application Insights dependency. That matters: Well over a hundred source files import this library, and
 * whatever it depends on lands in every one of their graphs.
 *
 * Provided by `provideInsights()` from `@pcp/insights`. When nothing provides it the logger writes
 * to the console and sends nothing, which is exactly what component workbenches, unit tests and
 * manual harnesses want — they register no sink rather than a stand-in.
 *
 * The logger decides *which* method to call for a given severity; the sink decides how to express
 * that on its own backend (Application Insights has its own 0-4 severity scale, which stays in
 * `@pcp/insights`).
 */
export interface TelemetrySink {
    /** A diagnostic message. */
    trace(level: LogLevel, message: string, properties?: Record<string, unknown>): void;

    /** A named business event. Never severity-gated — see `PCP_TELEMETRY_SINK`. */
    event(name: string, properties?: Record<string, unknown>): void;

    /** A failure, carrying the real `Error` wherever the caller had one. */
    exception(error: Error, level: LogLevel, properties?: Record<string, unknown>): void;

    /** A named numeric measurement. Never severity-gated. */
    metric(name: string, value: number, properties?: Record<string, unknown>): void;
}

/**
 * Optional telemetry destination.
 *
 * Inject with `{ optional: true }` — absence is a normal state, not an error.
 *
 * ⚠️ `event()` and `metric()` are **never** gated by a severity floor. Whether a business event
 * occurred is not a question of how verbose diagnostics are configured to be. Nothing downstream
 * filters for you either — the Application Insights SDK applies no severity filter of its own, so
 * every gate in this system is one we wrote.
 */
export const PCP_TELEMETRY_SINK = new InjectionToken<TelemetrySink>('PCP_TELEMETRY_SINK');
